import { Link, useLocation } from 'react-router-dom'
import { BarChart3, FileText, Settings, Menu, Server, Activity, Cpu, LineChart } from 'lucide-react'
import { useState, useEffect } from 'react'
import { API_BASE } from '../config'

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [serverStatus, setServerStatus] = useState<'online' | 'checking' | 'offline'>('checking')
  const location = useLocation()

  const navItems = [
    { path: '/', label: 'Dashboard', icon: BarChart3 },
    { path: '/ctolarsson', label: 'CTO Larsson Report', icon: LineChart },
    { path: '/posts', label: 'Processed Posts', icon: FileText },
    { path: '/settings', label: 'System Settings', icon: Settings },
  ]

  // Check backend server status
  useEffect(() => {
    const checkServer = async () => {
      try {
        const res = await fetch(`${API_BASE}/health`)
        if (res.ok) {
          setServerStatus('online')
        } else {
          setServerStatus('offline')
        }
      } catch (err) {
        setServerStatus('offline')
      }
    }
    checkServer()
    const interval = setInterval(checkServer, 15000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-100">
      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 w-64 glass-panel border-r border-slate-800/80 z-30 transition-all duration-300 transform 
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 flex flex-col`}
      >
        <div className="p-6 border-b border-slate-850 flex items-center gap-3">
          <div className="p-2.5 bg-cyan-500/10 border border-cyan-500/20 rounded-xl text-cyan-400">
            <Cpu size={22} className="pulse-animation" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-wider bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              BITCOIN.LIVE
            </h1>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">Scraper Panel</p>
          </div>
        </div>

        <nav className="p-4 flex-1 space-y-1.5">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.path
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3.5 px-4 py-3 rounded-xl transition-all duration-200 group relative ${
                  isActive
                    ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 text-cyan-400 shadow-[0_0_15px_-3px_rgba(6,182,212,0.15)]'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/50 border border-transparent'
                }`}
              >
                <Icon
                  size={18}
                  className={`transition-transform duration-300 group-hover:scale-110 ${
                    isActive ? 'text-cyan-400' : 'text-slate-400 group-hover:text-slate-300'
                  }`}
                />
                <span className="text-sm font-medium">{item.label}</span>
                {isActive && (
                  <span className="absolute right-3 w-1.5 h-1.5 bg-cyan-400 rounded-full glow-cyan" />
                )}
              </Link>
            )
          })}
        </nav>

        {/* Sidebar Footer Status */}
        <div className="p-4 border-t border-slate-850/80 bg-slate-950/20">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-xs text-slate-500 flex items-center gap-1.5 font-medium">
              <Server size={12} /> Server Node
            </span>
            {serverStatus === 'online' ? (
              <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/20 flex items-center gap-1 font-semibold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online
              </span>
            ) : serverStatus === 'checking' ? (
              <span className="text-[10px] bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded-full border border-amber-500/20 flex items-center gap-1 font-semibold uppercase tracking-wider animate-pulse">
                Checking
              </span>
            ) : (
              <span className="text-[10px] bg-rose-500/10 text-rose-400 px-2 py-0.5 rounded-full border border-rose-500/20 flex items-center gap-1 font-semibold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400" /> Offline
              </span>
            )}
          </div>
        </div>
      </aside>

      {/* Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <header className="bg-slate-950/60 backdrop-blur-md border-b border-slate-850/80 px-6 py-4 flex items-center justify-between z-10">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-900/60 rounded-lg border border-slate-800 transition-colors"
          >
            <Menu size={20} />
          </button>
          
          <div className="hidden md:flex items-center gap-2 text-xs text-slate-500">
            <Activity size={14} className="text-cyan-500" />
            <span>Scheduled scraper runs every 4 hours</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-xs px-2.5 py-1 rounded-md bg-slate-900 border border-slate-800 text-slate-400 font-mono">
              API Engine v1.0.0
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 md:p-8 bg-gradient-to-b from-slate-950 to-slate-900/40">
          <div className="max-w-6xl mx-auto space-y-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
